import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  BellOff,
  Bike,
  ChefHat,
  ClipboardList,
  History,
  MapPin,
  Navigation,
  PackageCheck,
  Phone,
  Printer,
  Receipt,
  Wifi,
  WifiOff,
} from 'lucide-react';
import {
  isTerminalOrder,
  ORDER_STATUS_LABELS,
  OrderStatus,
  PAYMENT_METHOD_LABELS,
  PaymentMethod,
  PaymentStatus,
  WS_EVENT,
  type CourierLocationPayload,
} from '@kick/types';
import { getErrorMessage } from '@/lib/api';
import { cn, desdeHace, formatCOP, formatDistance, formatFecha, formatHora } from '@/lib/cn';
import { imprimirComanda } from '@/lib/comanda';
import { distanciaMetros } from '@/lib/geo';
import { useConexion, useEventoSocket, type EstadoConexion } from '@/lib/socket';
import {
  useAccionPedido,
  useComercio,
  useHistorialPedidos,
  useMerchantId,
  usePedidosActivos,
  type PedidoComercio,
} from '@/lib/queries';
import { Boton, Cargando, ErrorCarga, Modal, Vacio } from '@/components/ui';
import { useAvisos } from '@/components/avisos';
import { RevisarLista } from '@/components/RevisarLista';

/** Columnas del tablero, en el orden en que avanza un pedido. */
const COLUMNAS = [
  {
    clave: 'nuevos',
    titulo: 'Nuevos',
    icono: Receipt,
    estados: [OrderStatus.PLACED],
    color: 'border-t-sky-500',
  },
  {
    clave: 'preparacion',
    titulo: 'En preparación',
    icono: ChefHat,
    estados: [OrderStatus.ACCEPTED, OrderStatus.PREPARING],
    color: 'border-t-indigo-500',
  },
  {
    clave: 'listos',
    titulo: 'Listos',
    icono: PackageCheck,
    estados: [OrderStatus.READY_FOR_PICKUP],
    color: 'border-t-violet-500',
  },
  {
    clave: 'camino',
    titulo: 'En camino',
    icono: Bike,
    estados: [OrderStatus.PICKED_UP, OrderStatus.ON_THE_WAY],
    color: 'border-t-emerald-500',
  },
] as const;

const MINUTOS_PREPARACION = [10, 15, 20, 30, 45, 60];

export function Pedidos() {
  const merchantId = useMerchantId();
  const conexion = useConexion();
  const activos = usePedidosActivos(merchantId, conexion === 'conectado');
  const comercio = useComercio(merchantId);
  const [verHistorial, setVerHistorial] = useState(false);
  const [sonido, setSonido] = useState(true);

  useTableroEnVivo(merchantId, conexion);
  useAvisoDePedidoNuevo(activos.data, sonido);

  if (activos.isLoading) return <Cargando texto="Cargando el tablero…" />;
  if (activos.isError) {
    return (
      <ErrorCarga mensaje={getErrorMessage(activos.error)} onReintentar={() => void activos.refetch()} />
    );
  }

  const pedidos = activos.data ?? [];

  return (
    <div className="mx-auto max-w-[1400px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">Pedidos</h1>
          <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-sm text-kick-gray">
            {pedidos.length === 0
              ? 'No hay pedidos en curso'
              : `${pedidos.length} pedido${pedidos.length === 1 ? '' : 's'} en curso`}
            <span aria-hidden>·</span>
            <IndicadorConexion estado={conexion} />
          </p>
        </div>

        <div className="flex gap-2">
          <Boton
            variante="secundario"
            icono={sonido ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
            onClick={() => setSonido((v) => !v)}
            aria-pressed={sonido}
          >
            {sonido ? 'Sonido activo' : 'Sonido apagado'}
          </Boton>
          <Boton
            variante="secundario"
            icono={<History className="h-4 w-4" />}
            onClick={() => setVerHistorial(true)}
          >
            Historial
          </Boton>
        </div>
      </div>

      {pedidos.length === 0 ? (
        <div className="mt-6">
          <Vacio
            icono={<ClipboardList className="h-6 w-6" />}
            titulo="Ningún pedido en curso"
            descripcion="Cuando entre un pedido aparecerá aquí, con aviso sonoro. Mantén el comercio abierto para recibirlos."
          />
        </div>
      ) : (
        <div className="mt-6 grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
          {COLUMNAS.map((columna) => {
            const suyos = pedidos.filter((p) => (columna.estados as readonly OrderStatus[]).includes(p.status));
            const Icono = columna.icono;

            return (
              <section
                key={columna.clave}
                className={cn('rounded-kick border-t-4 bg-white p-3 shadow-card', columna.color)}
              >
                <h2 className="flex items-center gap-2 px-1 pb-2 text-sm font-semibold uppercase tracking-wide text-kick-gray">
                  <Icono className="h-4 w-4" />
                  {columna.titulo}
                  <span className="ml-auto rounded-full bg-kick-light px-2 py-0.5 text-xs text-kick-ink">
                    {suyos.length}
                  </span>
                </h2>

                <div className="space-y-3">
                  {suyos.map((pedido) => (
                    <Tarjeta
                      key={pedido.id}
                      pedido={pedido}
                      merchantId={merchantId}
                      comercio={comercio.data?.name ?? ''}
                    />
                  ))}
                  {suyos.length === 0 && (
                    <p className="px-1 py-6 text-center text-sm text-kick-gray/60">Vacío</p>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <ModalHistorial
        abierto={verHistorial}
        onCerrar={() => setVerHistorial(false)}
        merchantId={merchantId}
      />
    </div>
  );
}

// ── Tarjeta de pedido ───────────────────────────────────────

function Tarjeta({
  pedido,
  merchantId,
  comercio,
}: {
  pedido: PedidoComercio;
  merchantId: string;
  comercio: string;
}) {
  const accion = useAccionPedido(merchantId);
  const avisos = useAvisos();
  const [aceptando, setAceptando] = useState(false);
  const [rechazando, setRechazando] = useState(false);
  const [revisandoLista, setRevisandoLista] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [posicion, setPosicion] = useState<{ metros: number } | null>(null);

  /**
   * Cuánto le falta al domiciliario.
   *
   * Responde a la pregunta que se hace el local cuando el cliente llama: «¿ya
   * va llegando?». Se calcula aquí con la posición que llega por el canal;
   * el servidor no necesita enterarse.
   */
  useEventoSocket<CourierLocationPayload>(
    WS_EVENT.COURIER_LOCATION,
    useCallback(
      (datos) => {
        if (datos.orderId !== pedido.id) return;
        setPosicion({ metros: distanciaMetros(datos.location, pedido.deliveryLocation) });
      },
      [pedido.id, pedido.deliveryLocation],
    ),
  );

  const ejecutar = (datos: Parameters<typeof accion.mutate>[0], exito: string): void => {
    accion.mutate(datos, {
      onSuccess: () => {
        setAceptando(false);
        setRechazando(false);
        avisos.exito(exito);
      },
      onError: (error) => avisos.error(getErrorMessage(error)),
    });
  };

  return (
    <article className="rounded-kick border border-kick-gray/15 p-3">
      <header className="flex items-start justify-between gap-2">
        <div>
          <p className="font-mono font-bold text-kick-dark">{pedido.code}</p>
          {/* La espera es lo que más importa en el tablero: un pedido de hace
              20 minutos sin aceptar es un cliente a punto de irse. */}
          <p className="text-xs text-kick-gray">
            {formatHora(pedido.placedAt ?? pedido.createdAt)} ·{' '}
            {desdeHace(pedido.placedAt ?? pedido.createdAt)}
          </p>
        </div>
        <span className="font-bold text-kick-dark">{formatCOP(pedido.total)}</span>
      </header>

      <ul className="mt-2.5 space-y-1 border-y border-kick-gray/10 py-2 text-sm">
        {pedido.items.map((item) => (
          <li key={item.id} className="flex gap-1.5">
            <span className="font-semibold text-kick-red">{item.quantity}×</span>
            <span className="min-w-0 flex-1">
              <span className="block text-kick-ink">{item.productName}</span>
              {item.options.length > 0 && (
                <span className="block text-xs text-kick-gray">
                  {item.options.map((o) => o.optionName).join(' · ')}
                </span>
              )}
              {item.notes && (
                <span className="block text-xs italic text-amber-700">«{item.notes}»</span>
              )}
            </span>
          </li>
        ))}
      </ul>

      {pedido.customerNotes && (
        <p className="mt-2 rounded-kick bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
          <span className="font-semibold">Nota del cliente: </span>
          {pedido.customerNotes}
        </p>
      )}

      <div className="mt-2 space-y-1 text-xs text-kick-gray">
        <p className="flex items-start gap-1.5">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {pedido.deliveryAddress.addressLine}
            {pedido.deliveryAddress.neighborhood && ` · ${pedido.deliveryAddress.neighborhood}`}
            {' · '}
            {formatDistance(pedido.distanceMeters)}
          </span>
        </p>
        {pedido.deliveryAddress.contactPhone && (
          <p className="flex items-center gap-1.5">
            <Phone className="h-3.5 w-3.5 shrink-0" />
            {pedido.deliveryAddress.contactPhone}
          </p>
        )}
        {/* Una transferencia sin verificar no es lo mismo que un pedido pagado. */}
        <p
          className={cn(
            pedido.paymentMethod === PaymentMethod.TRANSFER &&
              pedido.paymentStatus !== PaymentStatus.PAID &&
              'font-semibold text-amber-700',
          )}
        >
          {PAYMENT_METHOD_LABELS[pedido.paymentMethod]}
          {pedido.paymentStatus === PaymentStatus.PAID
            ? ' · pagado'
            : pedido.paymentMethod === PaymentMethod.TRANSFER
              ? ' · sin verificar'
              : ' · cobrar al entregar'}
        </p>
      </div>

      {/* Acciones según el estado. Las transiciones válidas son las de
          @kick/types: la API rechaza cualquier otra. */}
      <div className="mt-3 space-y-2">
        {pedido.status === OrderStatus.PLACED && (
          <>
            {pedido.listStage && pedido.listStage !== 'APPROVED' ? (
              <>
                <Boton className="w-full" onClick={() => setRevisandoLista(true)}>
                  {pedido.listStage === 'QUOTING' ? 'Cotizar la lista' : 'Ver la lista'}
                </Boton>
                <p className="text-center text-xs text-kick-gray">
                  {pedido.listStage === 'QUOTING'
                    ? 'Pedido por lista: ponle disponibilidad y precio a cada renglón.'
                    : 'Cotizada: el cliente está confirmando el total.'}
                </p>
              </>
            ) : (
              <>
                <Boton
                  className="w-full"
                  cargando={accion.isPending}
                  onClick={() => setAceptando(true)}
                >
                  Aceptar
                </Boton>
                {pedido.listStage && (
                  <Boton
                    variante="secundario"
                    className="w-full"
                    onClick={() => setRevisandoLista(true)}
                  >
                    Ver la lista confirmada
                  </Boton>
                )}
              </>
            )}
            <Boton
              variante="peligro"
              className="w-full"
              disabled={accion.isPending}
              onClick={() => setRechazando(true)}
            >
              Rechazar
            </Boton>
          </>
        )}

        {pedido.status === OrderStatus.ACCEPTED && (
          <Boton
            className="w-full"
            cargando={accion.isPending}
            icono={<ChefHat className="h-4 w-4" />}
            onClick={() =>
              ejecutar(
                { id: pedido.id, accion: 'estado', status: OrderStatus.PREPARING },
                `${pedido.code} en preparación`,
              )
            }
          >
            Empezar a preparar
          </Boton>
        )}

        {pedido.status === OrderStatus.PREPARING && (
          <Boton
            className="w-full"
            cargando={accion.isPending}
            icono={<PackageCheck className="h-4 w-4" />}
            onClick={() =>
              ejecutar(
                { id: pedido.id, accion: 'estado', status: OrderStatus.READY_FOR_PICKUP },
                `${pedido.code} listo para recoger`,
              )
            }
          >
            Marcar listo
          </Boton>
        )}

        {pedido.status === OrderStatus.READY_FOR_PICKUP && (
          <p className="rounded-kick bg-violet-50 px-2.5 py-2 text-center text-xs font-semibold text-violet-800">
            {pedido.courierId ? 'Esperando al domiciliario' : 'Buscando domiciliario'}
          </p>
        )}

        {(pedido.status === OrderStatus.PICKED_UP || pedido.status === OrderStatus.ON_THE_WAY) && (
          <>
            <p className="rounded-kick bg-emerald-50 px-2.5 py-2 text-center text-xs font-semibold text-emerald-800">
              {ORDER_STATUS_LABELS[pedido.status]}
            </p>
            {/* Cuánto le falta al domiciliario, con la posición que llega en vivo. */}
            {posicion && (
              <p className="flex items-center justify-center gap-1.5 text-xs text-kick-gray">
                <Navigation className="h-3.5 w-3.5" />
                a {formatDistance(posicion.metros)} del cliente
              </p>
            )}
          </>
        )}

        {/* La comanda va a la cocina; se puede reimprimir en cualquier momento. */}
        <button
          type="button"
          onClick={() => imprimirComanda(pedido, comercio)}
          className="flex w-full items-center justify-center gap-1.5 rounded-kick py-1.5 text-xs font-semibold text-kick-gray transition hover:bg-kick-light hover:text-kick-ink"
        >
          <Printer className="h-3.5 w-3.5" />
          Imprimir comanda
        </button>
      </div>

      <RevisarLista
        pedido={pedido}
        abierto={revisandoLista}
        onCerrar={() => setRevisandoLista(false)}
      />

      {/* Aceptar: el tiempo de preparación se ajusta aquí porque cambia la
          hora estimada que ve el cliente. */}
      <Modal
        abierto={aceptando}
        onCerrar={() => setAceptando(false)}
        titulo={`Aceptar ${pedido.code}`}
        descripcion="¿En cuánto tiempo estará listo?"
        ancho="sm"
      >
        <div className="p-5">
          <div className="grid grid-cols-3 gap-2">
            {MINUTOS_PREPARACION.map((minutos) => (
              <Boton
                key={minutos}
                variante="secundario"
                cargando={accion.isPending}
                onClick={() =>
                  ejecutar(
                    { id: pedido.id, accion: 'aceptar', prepMinutes: minutos },
                    `${pedido.code} aceptado`,
                  )
                }
              >
                {minutos} min
              </Boton>
            ))}
          </div>
          <Boton
            variante="fantasma"
            className="mt-3 w-full"
            disabled={accion.isPending}
            onClick={() =>
              ejecutar({ id: pedido.id, accion: 'aceptar' }, `${pedido.code} aceptado`)
            }
          >
            Usar el tiempo habitual del comercio
          </Boton>
        </div>
      </Modal>

      {/* Rechazar: el motivo es obligatorio, el cliente merece saber por qué. */}
      <Modal
        abierto={rechazando}
        onCerrar={() => setRechazando(false)}
        titulo={`Rechazar ${pedido.code}`}
        descripcion="El cliente verá el motivo y no se le cobrará."
        ancho="sm"
      >
        <div className="p-5">
          <label htmlFor="motivo" className="mb-1.5 block text-sm font-semibold text-kick-ink">
            Motivo
          </label>
          <input
            id="motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="No tenemos ingredientes para ese plato"
            maxLength={200}
            className="w-full rounded-kick border border-kick-gray/30 bg-white px-3.5 py-2.5 text-kick-ink outline-none transition focus:border-kick-red focus:ring-2 focus:ring-kick-red/20"
          />
          <div className="mt-6 flex justify-end gap-2">
            <Boton variante="secundario" onClick={() => setRechazando(false)}>
              Volver
            </Boton>
            <Boton
              variante="peligro"
              cargando={accion.isPending}
              disabled={motivo.trim().length < 3}
              onClick={() =>
                ejecutar(
                  { id: pedido.id, accion: 'rechazar', reason: motivo.trim() },
                  `${pedido.code} rechazado`,
                )
              }
            >
              Rechazar pedido
            </Boton>
          </div>
        </div>
      </Modal>
    </article>
  );
}

// ── Historial ───────────────────────────────────────────────

function ModalHistorial({
  abierto,
  onCerrar,
  merchantId,
}: {
  abierto: boolean;
  onCerrar: () => void;
  merchantId: string;
}) {
  const historial = useHistorialPedidos(merchantId);

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Historial de pedidos"
      descripcion="Los últimos 30 pedidos del comercio."
      ancho="lg"
    >
      <div className="max-h-[70vh] overflow-y-auto p-5">
        {historial.isLoading && <Cargando />}

        {historial.data?.data.length === 0 && (
          <p className="py-8 text-center text-sm text-kick-gray">Todavía no hay pedidos.</p>
        )}

        <ul className="divide-y divide-kick-gray/10">
          {historial.data?.data.map((pedido) => (
            <li key={pedido.id} className="flex flex-wrap items-center gap-3 py-3">
              <span className="font-mono text-sm font-bold text-kick-dark">{pedido.code}</span>
              <span className="text-sm text-kick-gray">{formatFecha(pedido.createdAt)}</span>
              <span
                className={cn(
                  'rounded-full px-2.5 py-0.5 text-xs font-semibold',
                  pedido.status === OrderStatus.DELIVERED
                    ? 'bg-emerald-100 text-emerald-800'
                    : isTerminalOrder(pedido.status)
                      ? 'bg-red-100 text-red-800'
                      : 'bg-sky-100 text-sky-800',
                )}
              >
                {ORDER_STATUS_LABELS[pedido.status]}
              </span>
              <span className="ml-auto text-right">
                <span className="block font-semibold text-kick-ink">{formatCOP(pedido.total)}</span>
                <span className="block text-xs text-kick-gray">
                  comisión {formatCOP(pedido.commissionAmount)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

// ── Canal en vivo ───────────────────────────────────────────

/**
 * Mantiene el tablero al día con los eventos del servidor.
 *
 * Cada evento invalida la consulta en vez de parchear la caché a mano. Cuesta
 * una petición más, pero garantiza que lo que se ve es lo que hay: aplicar
 * cambios parciales sobre un pedido que quizá cambió por otro lado es la vía
 * rápida a un tablero que muestra un estado que ya no existe.
 *
 * Al reconectar se invalida también: mientras el socket estuvo caído pudo
 * entrar un pedido, y el evento se perdió.
 */
function useTableroEnVivo(merchantId: string, conexion: EstadoConexion): void {
  const qc = useQueryClient();
  const refrescar = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['pedidos', merchantId] });
  }, [qc, merchantId]);

  useEventoSocket(WS_EVENT.ORDER_CREATED, refrescar);
  useEventoSocket(WS_EVENT.ORDER_STATUS_CHANGED, refrescar);
  useEventoSocket(WS_EVENT.COURIER_ASSIGNED, refrescar);
  useEventoSocket(WS_EVENT.LIST_UPDATED, refrescar);

  useEffect(() => {
    if (conexion === 'conectado') refrescar();
  }, [conexion, refrescar]);
}

function IndicadorConexion({ estado }: { estado: EstadoConexion }) {
  if (estado === 'conectado') {
    return (
      <span className="inline-flex items-center gap-1.5 text-emerald-600">
        <Wifi className="h-3.5 w-3.5" />
        en vivo
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5',
        estado === 'conectando' ? 'text-amber-600' : 'text-red-600',
      )}
    >
      <WifiOff className="h-3.5 w-3.5" />
      {estado === 'conectando' ? 'reconectando…' : 'sin conexión, consultando cada 10 s'}
    </span>
  );
}

// ── Aviso de pedido nuevo ───────────────────────────────────

/**
 * Suena y notifica cuando entra un pedido que no estaba antes.
 *
 * El tono se genera con la Web Audio API en vez de cargar un archivo: es un
 * bip corto y no agrega un recurso más que descargar. Los navegadores no
 * dejan sonar hasta que el usuario interactúa con la página, así que el
 * primer bip puede perderse — por eso el aviso visual va igual.
 */
function useAvisoDePedidoNuevo(pedidos: PedidoComercio[] | undefined, activo: boolean): void {
  const avisos = useAvisos();
  const vistos = useRef<Set<string> | null>(null);

  const nuevos = useMemo(
    () => (pedidos ?? []).filter((p) => p.status === OrderStatus.PLACED).map((p) => p.id),
    [pedidos],
  );

  useEffect(() => {
    // La primera carga no avisa: son los pedidos que ya estaban.
    if (vistos.current === null) {
      vistos.current = new Set(nuevos);
      return;
    }

    const recien = nuevos.filter((id) => !vistos.current!.has(id));
    vistos.current = new Set(nuevos);
    if (recien.length === 0) return;

    avisos.exito(
      recien.length === 1 ? 'Entró un pedido nuevo' : `Entraron ${recien.length} pedidos nuevos`,
    );

    if (activo) sonar();
    // avisos se reconstruye en cada render; solo interesa reaccionar a la lista.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nuevos.join(','), activo]);
}

function sonar(): void {
  try {
    const contexto = new AudioContext();
    const oscilador = contexto.createOscillator();
    const volumen = contexto.createGain();

    oscilador.type = 'sine';
    oscilador.frequency.setValueAtTime(880, contexto.currentTime);
    oscilador.frequency.setValueAtTime(1174, contexto.currentTime + 0.12);

    volumen.gain.setValueAtTime(0.18, contexto.currentTime);
    volumen.gain.exponentialRampToValueAtTime(0.001, contexto.currentTime + 0.5);

    oscilador.connect(volumen).connect(contexto.destination);
    oscilador.start();
    oscilador.stop(contexto.currentTime + 0.5);
    oscilador.onended = () => void contexto.close();
  } catch {
    // Sin audio disponible el aviso visual basta.
  }
}

